import React from "react";
import { Tag, Tooltip, Input, Button, Space, Popover } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import {
  SocketMessageInterface,
  TerminalRowInterface,
} from "../../../interfaces/Monitoring.interface";
import { socketMessageToIcon } from "../../../utils/bitmaskToIcon";
import IconDefinition from "../iconDefinition/IconDefinition";
import { Usage } from "../../../flatbuffers/generated/usage/usage";
import { DeviceDirection } from "../../../types/DeviceDirection";
import { directionDisplayName } from "../../../flatbuffers/types/enumDisplayUtils";
import {
  messageTypeDisplayName,
  msgInnerDisplayName,
  msgValueDisplayName,
} from "../../../flatbuffers/types/messageDisplayUtils";
import { uuidToString } from "../../../utils/uuid";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { controlLeaf } from "../../../flatbuffers/decoders/uiCmdDecoder";
import { isFloorBasedControl, takesOutputNumber } from "../../../utils/controlFloorRules";

type TerminalColumnsOptions = {
  terminalShowCarHallCalls: boolean;
  liftNameMap?: Record<string, string>;
  terminalIconMode?: "svg" | "text";
};

const TAG_COLOR_BY_TYPE: any = {
  FAULT: "volcano",
  TRAFFIC: "gold",
  USAGE: "magenta",
  STATUS: "blue",
};

/**
 * Categorical filter list for Msg Type — derived from MessageContent enum.
 * Skips NONE so the dropdown only offers families that can actually appear
 * in the stream.
 */
const MSG_TYPE_FILTERS = (Object.values(MessageContent) as (number | string)[])
  .filter((v): v is number => typeof v === "number" && v !== MessageContent.NONE)
  .map((v) => ({
    text: messageTypeDisplayName(v as MessageContent),
    value: v,
  }));

/**
 * Build a `filterDropdown` config for free-text search against a string
 * derived from each row. Pattern adapted from Antd's example column filter
 * dropdown — gives a small input + Search/Reset, plus a column-header
 * search icon that highlights when a filter is active.
 */
function textFilterColumn<T>(getText: (row: T) => string): {
  filterDropdown: (props: {
    setSelectedKeys: (keys: React.Key[]) => void;
    selectedKeys: React.Key[];
    confirm: () => void;
    clearFilters?: () => void;
  }) => React.ReactNode;
  filterIcon: (filtered: boolean) => React.ReactNode;
  onFilter: (value: boolean | React.Key, record: T) => boolean;
} {
  return {
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder="Filter…"
          // Keep keystrokes scoped to the filter — without this, typing
          // hotkey letters would bubble up to the page-level shortcut bar.
          onKeyDown={(e) => e.stopPropagation()}
          value={selectedKeys[0] as string}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: "block", width: 160 }}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => confirm()}
            style={{ width: 80 }}
          >
            Search
          </Button>
          <Button
            size="small"
            onClick={() => {
              clearFilters?.();
              confirm();
            }}
            style={{ width: 80 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) => {
      const needle = String(value ?? "").toLowerCase();
      if (!needle) return true;
      return getText(record).toLowerCase().includes(needle);
    },
  };
}

const BOOLEAN_FILTERS = [
  { text: "True", value: true },
  { text: "False", value: false },
];

const DIRECTION_FILTERS = [
  { text: "Up", value: Usage.Up },
  { text: "Down", value: Usage.Down },
  { text: "Idle", value: -1 }, // sentinel for "neither Up nor Down"
];

const DOOR_FILTERS = [
  { text: "Open", value: true },
  { text: "Close", value: false },
];

const DIR_FILTERS = [
  { text: "TX (sent)", value: "TX" },
  { text: "RX (received)", value: "RX" },
];

/**
 * Label for the "Message" column.
 *
 * Inbound rows resolve their leaf enum out of the BitMask/OneHot envelope.
 * Outbound rows have no envelope — a UiCmd is its own FlatBuffer root — so
 * their label comes from `txMeta.commandName`, which the decoder read off the
 * sent bytes via the generated `Control` enum.
 */
export function terminalMessageLabel(row: TerminalRowInterface): string {
  if (row.txMeta) return row.txMeta.commandName;
  return msgInnerDisplayName({
    liftType: LiftType.ELEVATOR,
    contentType: row.msg?.contentType,
    messageType: row.msg?.messageType,
    content: row.msg?.content,
  });
}

/** "TX" for a row this browser sent, "RX" for one the controller sent us. */
export function terminalRowDirection(row: TerminalRowInterface): "TX" | "RX" {
  return row.txMeta ? "TX" : "RX";
}

/**
 * Label for the "Value" column.
 *
 * Inbound rows pull a scalar out of the BitMask/OneHot envelope via
 * `msgValueDisplayName`. Outbound rows have no envelope — `uiCmdDecoder`
 * deliberately leaves `msg.content` null there (see its doc comment) — so
 * they fell through to "--" even for a car/hall call that does carry a
 * meaningful value: the floor the command targets.
 *
 * That floor already decoded onto `row.position` (from
 * `ElevatorControlMessageT.position`), but `position` is a required,
 * non-nullable FlatBuffer scalar that defaults to 0 — it is never actually
 * absent, even for door-agnostic commands like VIP or CloseDoor that were
 * sent with no floor in mind. Gating on `row.position == null` would render
 * "0" for those and imply a floor that was never targeted. Gate on whether
 * the command is floor-based instead, via the same classification the
 * control-panel floor-button strip uses (`isFloorBasedControl`), so only
 * genuine floor commands — car and hall calls, and ShuttleFloor — show a
 * floor here.
 *
 * GenericOutput also carries a meaningful position, but it is the output
 * number (1-32) rather than a floor; the Message column names the command, so
 * the bare number reads as the output it switched.
 */
export function terminalValueLabel(row: TerminalRowInterface): string {
  if (row.txMeta) {
    const leaf = row.txMeta.uiCmd ? controlLeaf(row.txMeta.uiCmd) : null;
    if (
      leaf !== null &&
      (isFloorBasedControl(leaf) || takesOutputNumber(leaf)) &&
      row.position != null
    ) {
      return String(row.position);
    }
    return "--";
  }
  return msgValueDisplayName({
    contentType: row.msg?.contentType,
    messageType: row.msg?.messageType,
    content: row.msg?.content,
  });
}

/**
 * Hex dump of an outbound frame, wrapped at 16 bytes per line so a reader can
 * count offsets. `hex` arrives space-separated, one pair per byte.
 */
function formatHexBlock(hex: string): string {
  const bytes = hex.split(" ");
  const lines: string[] = [];
  for (let i = 0; i < bytes.length; i += 16) {
    lines.push(bytes.slice(i, i + 16).join(" "));
  }
  return lines.join("\n");
}

export const TerminalTableColumns = ({
  terminalShowCarHallCalls,
  liftNameMap,
  terminalIconMode = "svg",
}: TerminalColumnsOptions) => {
  // Build the Car filter list from the lift name map. Each entry's `value`
  // is the liftId so onFilter can compare row.liftId without fuzzy text.
  const carFilters: { text: string; value: string }[] = liftNameMap
    ? Object.entries(liftNameMap).map(([id, name]) => ({ text: name, value: id }))
    : [];

  return [
    {
      show: true,
      title: "No",
      key: "no",
      width: 50,
      render: (_: any, __: any, index: number) => <Tag color="gold">{index + 1}</Tag>,
    },
    {
      show: true,
      title: "Dir",
      key: "direction_tx_rx",
      width: 64,
      filters: DIR_FILTERS,
      onFilter: (value: boolean | React.Key, row: TerminalRowInterface) =>
        terminalRowDirection(row) === value,
      render: (_: unknown, row: TerminalRowInterface) => {
        if (!row.txMeta) {
          return <Tag color="default">RX</Tag>;
        }
        const { txMeta } = row;
        return (
          <Popover
            placement="right"
            title={`UiCmd #${txMeta.seq} · ${txMeta.byteLength} bytes`}
            content={
              <div style={{ maxWidth: 420 }}>
                <div style={{ fontSize: 12, marginBottom: 6 }}>
                  <div>
                    <strong>Command:</strong> Control::{txMeta.commandName}
                  </div>
                  <div>
                    <strong>Server:</strong> {txMeta.serverName || txMeta.serverId}
                  </div>
                  <div>
                    <strong>Channel:</strong> {txMeta.channelId}
                  </div>
                  <div>
                    <strong>Sent:</strong> {new Date(txMeta.sentAt).toISOString()}
                  </div>
                </div>
                <pre
                  style={{
                    fontSize: 11,
                    margin: 0,
                    whiteSpace: "pre",
                    maxHeight: 200,
                    overflow: "auto",
                  }}
                >
                  {formatHexBlock(txMeta.hex)}
                </pre>
              </div>
            }
          >
            <Tag color="volcano" style={{ cursor: "pointer" }}>
              TX
            </Tag>
          </Popover>
        );
      },
    },
    {
      show: !!liftNameMap && Object.keys(liftNameMap).length > 0,
      title: "Car",
      key: "car_name",
      width: 90,
      filters: carFilters.length > 0 ? carFilters : undefined,
      onFilter: (value: boolean | React.Key, row: SocketMessageInterface) => {
        const liftIdStr = typeof row.liftId === "string" ? row.liftId : uuidToString(row.liftId);
        return liftIdStr === value;
      },
      render: (_: unknown, row: SocketMessageInterface) => {
        const liftIdStr = typeof row.liftId === "string" ? row.liftId : uuidToString(row.liftId);
        const bankIdStr = typeof row.bankId === "string" ? row.bankId : uuidToString(row.bankId);
        if (liftIdStr && bankIdStr && liftIdStr === bankIdStr) {
          return <Tag color="geekblue">Bank</Tag>;
        }
        const label = liftNameMap?.[liftIdStr] ?? liftIdStr?.substring(0, 8) ?? "-";
        return <Tag color="purple">{label}</Tag>;
      },
    },
    {
      show: true,
      title: "Icon",
      key: "icon",
      width: 50,
      ...textFilterColumn<SocketMessageInterface>((row) => socketMessageToIcon(row) ?? ""),
      render: (_: any, row: SocketMessageInterface) => {
        const iconName = socketMessageToIcon(row);
        if (!iconName) return null;
        if (terminalIconMode === "text") {
          const colorMap: Record<string, string> = {
            elUp: "green",
            elDown: "red",
            elOpening: "gold",
            elOpen: "blue",
            elClose: "purple",
            elFrontCloseRearClose: "purple",
            elFrontCloseRearOpen: "cyan",
            elFrontOpenRearClose: "cyan",
            elFrontOpenRearOpen: "blue",
            elWarning: "orange",
          };
          return <Tag color={colorMap[iconName] ?? "default"}>{iconName.replace(/^el/, "")}</Tag>;
        }
        return (
          <Tooltip title={iconName}>
            <div style={{ width: 24, height: 24 }}>
              <IconDefinition
                isMap={false}
                expanded={false}
                liftType={LiftType[LiftType.ELEVATOR]}
                icon={iconName}
                iconSize={20}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      show: true,
      title: "Position",
      key: "position",
      width: 70,
      dataIndex: "position",
      ...textFilterColumn<SocketMessageInterface>((row) => String(row.position ?? "")),
      render: (value: number) => <Tag color="cyan">{value}</Tag>,
    },
    {
      show: true,
      title: "Msg Active",
      dataIndex: ["msg", "active"],
      key: "msg_active",
      filters: BOOLEAN_FILTERS,
      onFilter: (value: boolean | React.Key, row: SocketMessageInterface) =>
        Boolean(row.msg?.active) === Boolean(value),
      render: (value: boolean) => (
        <>
          {value ? (
            <Tag color="green" className="generalStyles__width100px generalStyles__textCenter">
              True
            </Tag>
          ) : (
            <Tag color="red" className="generalStyles__width100px generalStyles__textCenter">
              False
            </Tag>
          )}
        </>
      ),
    },
    {
      show: true,
      title: "Msg Type",
      key: "msg_type",
      filters: MSG_TYPE_FILTERS,
      onFilter: (value: boolean | React.Key, row: SocketMessageInterface) =>
        Number(row.msg?.messageType ?? -1) === Number(value),
      render: (_: unknown, row: SocketMessageInterface) => {
        const label = messageTypeDisplayName(row.msg?.messageType);
        const color = TAG_COLOR_BY_TYPE[label.toUpperCase()] || "default";
        return (
          <Tag color={color} className="generalStyles__width100px generalStyles__textCenter">
            {label}
          </Tag>
        );
      },
    },
    {
      show: true,
      title: "Message",
      key: "msg_inner",
      ...textFilterColumn<TerminalRowInterface>((row) => terminalMessageLabel(row)),
      render: (_: unknown, row: TerminalRowInterface) => terminalMessageLabel(row),
    },
    {
      show: true,
      title: "Value",
      key: "msg_value",
      width: 80,
      ...textFilterColumn<TerminalRowInterface>((row) => terminalValueLabel(row)),
      render: (_: unknown, row: TerminalRowInterface) => terminalValueLabel(row),
    },
    {
      show: true,
      title: "Direction",
      dataIndex: "direction",
      key: "direction",
      filters: DIRECTION_FILTERS,
      onFilter: (value: boolean | React.Key, row: SocketMessageInterface) => {
        if (Number(value) === Usage.Up) return row.direction === Usage.Up;
        if (Number(value) === Usage.Down) return row.direction === Usage.Down;
        // Idle = anything else (sentinel)
        return row.direction !== Usage.Up && row.direction !== Usage.Down;
      },
      render: (value: DeviceDirection) => {
        const label = directionDisplayName(value);
        let color = "gray";
        if (value === Usage.Up) color = "green";
        else if (value === Usage.Down) color = "red";
        return (
          <Tag color={color} className="generalStyles__width100px generalStyles__textCenter">
            {label}
          </Tag>
        );
      },
    },
    {
      show: true,
      title: "Door Status Front",
      dataIndex: ["doorStatus", "frontOpen"],
      key: "door_status_front_open",
      filters: DOOR_FILTERS,
      onFilter: (value: boolean | React.Key, row: SocketMessageInterface) =>
        Boolean(row.doorStatus?.frontOpen) === Boolean(value),
      render: (text: boolean) => (
        <>
          {text ? (
            <Tag color="green" className="generalStyles__width100px generalStyles__textCenter">
              Open
            </Tag>
          ) : (
            <Tag color="red" className="generalStyles__width100px generalStyles__textCenter">
              Close
            </Tag>
          )}
        </>
      ),
    },
    {
      show: true,
      title: "Door Status Rear",
      dataIndex: ["doorStatus", "rearOpen"],
      key: "door_status_rear_open",
      filters: DOOR_FILTERS,
      onFilter: (value: boolean | React.Key, row: SocketMessageInterface) =>
        Boolean(row.doorStatus?.rearOpen) === Boolean(value),
      render: (text: boolean) => (
        <>
          {text ? (
            <Tag color="green" className="terminal__typeTag">
              Open
            </Tag>
          ) : (
            <Tag color="red" className="terminal__typeTag">
              Close
            </Tag>
          )}
        </>
      ),
    },

    {
      show: true,
      title: "Faults",
      dataIndex: "faults",
      key: "faults",
      filters: BOOLEAN_FILTERS,
      onFilter: (value: boolean | React.Key, row: SocketMessageInterface) =>
        Boolean(row.faults) === Boolean(value),
      render: (text: boolean) => (
        <>
          {text ? (
            <Tag color="red" className="generalStyles__width100px generalStyles__textCenter">
              True
            </Tag>
          ) : (
            <Tag color="green" className="generalStyles__width100px generalStyles__textCenter">
              False
            </Tag>
          )}
        </>
      ),
    },
    {
      show: terminalShowCarHallCalls,
      title: "Car Calls",
      dataIndex: "carCalls",
      key: "car_calls",
      ...textFilterColumn<SocketMessageInterface>((row) =>
        [...(row.carCalls?.front ?? []), ...(row.carCalls?.rear ?? [])].join(","),
      ),
      render: (text: boolean, row: SocketMessageInterface) => (
        <>
          {row.carCalls.front.map((carCall: number) => (
            <Tag key={`front-${carCall}`} className="terminal__positionTag" color="blue">
              {carCall}
            </Tag>
          ))}
          {row.carCalls.rear.map((carCall: number) => (
            <Tag key={`rear-${carCall}`} className="terminal__positionTag" color="blue">
              {carCall}
            </Tag>
          ))}
        </>
      ),
    },
    {
      show: terminalShowCarHallCalls,
      title: "Lock Car Calls",
      dataIndex: "lock_car_calls",
      key: "lock_car_calls",
      ...textFilterColumn<SocketMessageInterface>((row) =>
        [...(row.carCalls?.frontLock ?? []), ...(row.carCalls?.rearLock ?? [])].join(","),
      ),
      render: (text: boolean, row: SocketMessageInterface) => (
        <>
          {row.carCalls.frontLock.map((carCall: number) => (
            <Tag key={`front-lock-${carCall}`} className="terminal__positionTag" color="orange">
              {carCall}
            </Tag>
          ))}
          {row.carCalls.rearLock.map((carCall: number) => (
            <Tag key={`rear-lock-${carCall}`} className="terminal__positionTag" color="orange">
              {carCall}
            </Tag>
          ))}
        </>
      ),
    },
    {
      show: terminalShowCarHallCalls,
      title: "Hall Calls",
      dataIndex: "hallCalls",
      key: "hall_calls",
      ...textFilterColumn<SocketMessageInterface>((row) =>
        [
          ...(row.hallCalls?.up?.front ?? []),
          ...(row.hallCalls?.up?.rear ?? []),
          ...(row.hallCalls?.down?.front ?? []),
          ...(row.hallCalls?.down?.rear ?? []),
        ].join(","),
      ),
      render: (text: boolean, row: SocketMessageInterface) => (
        <>
          {row.hallCalls.up.front.map((hallCall: number) => (
            <Tag key={`up-front-${hallCall}`} className="terminal__positionTag" color="green">
              {hallCall}
            </Tag>
          ))}
          {row.hallCalls.up.rear.map((hallCall: number) => (
            <Tag key={`up-rear-${hallCall}`} className="terminal__positionTag" color="green">
              {hallCall}
            </Tag>
          ))}
          {row.hallCalls.down.front.map((hallCall: number) => (
            <Tag key={`down-front-${hallCall}`} className="terminal__positionTag" color="red">
              {hallCall}
            </Tag>
          ))}
          {row.hallCalls.down.rear.map((hallCall: number) => (
            <Tag key={`down-rear-${hallCall}`} className="terminal__positionTag" color="red">
              {hallCall}
            </Tag>
          ))}
        </>
      ),
    },
    {
      show: terminalShowCarHallCalls,
      title: "Assigned Hall Calls",
      dataIndex: "assigned_hall_calls",
      key: "assigned_hall_calls",
      ...textFilterColumn<SocketMessageInterface>((row) =>
        [
          ...(row.hallCalls?.upAssigned?.front ?? []),
          ...(row.hallCalls?.upAssigned?.rear ?? []),
          ...(row.hallCalls?.downAssigned?.front ?? []),
          ...(row.hallCalls?.downAssigned?.rear ?? []),
        ].join(","),
      ),
      render: (text: boolean, row: SocketMessageInterface) => (
        <>
          {row.hallCalls.upAssigned.front.map((hallCall: number) => (
            <Tag
              key={`up-assigned-front-${hallCall}`}
              className="terminal__positionTag"
              color="green"
            >
              {hallCall}
            </Tag>
          ))}
          {row.hallCalls.upAssigned.rear.map((hallCall: number) => (
            <Tag
              key={`up-assigned-rear-${hallCall}`}
              className="terminal__positionTag"
              color="green"
            >
              {hallCall}
            </Tag>
          ))}
          {row.hallCalls.downAssigned.front.map((hallCall: number) => (
            <Tag
              key={`down-assigned-front-${hallCall}`}
              className="terminal__positionTag"
              color="red"
            >
              {hallCall}
            </Tag>
          ))}
          {row.hallCalls.downAssigned.rear.map((hallCall: number) => (
            <Tag
              key={`down-assigned-rear-${hallCall}`}
              className="terminal__positionTag"
              color="red"
            >
              {hallCall}
            </Tag>
          ))}
        </>
      ),
    },
    {
      show: terminalShowCarHallCalls,
      title: "Lock Hall Calls",
      dataIndex: "lock_hall_calls",
      key: "lock_hall_calls",
      ...textFilterColumn<SocketMessageInterface>((row) =>
        [
          ...(row.hallCalls?.up?.frontLock ?? []),
          ...(row.hallCalls?.up?.rearLock ?? []),
          ...(row.hallCalls?.down?.frontLock ?? []),
          ...(row.hallCalls?.down?.rearLock ?? []),
        ].join(","),
      ),
      render: (text: boolean, row: SocketMessageInterface) => (
        <>
          {row.hallCalls.up.frontLock.map((hallCall: number) => (
            <Tag key={`up-front-lock-${hallCall}`} className="terminal__positionTag" color="lime">
              {hallCall}
            </Tag>
          ))}
          {row.hallCalls.up.rearLock.map((hallCall: number) => (
            <Tag key={`up-rear-lock-${hallCall}`} className="terminal__positionTag" color="lime">
              {hallCall}
            </Tag>
          ))}
          {row.hallCalls.down.frontLock.map((hallCall: number) => (
            <Tag
              key={`down-front-lock-${hallCall}`}
              className="terminal__positionTag"
              color="orange"
            >
              {hallCall}
            </Tag>
          ))}
          {row.hallCalls.down.rearLock.map((hallCall: number) => (
            <Tag
              key={`down-rear-lock-${hallCall}`}
              className="terminal__positionTag"
              color="orange"
            >
              {hallCall}
            </Tag>
          ))}
        </>
      ),
    },
    {
      show: terminalShowCarHallCalls,
      title: "Lock Assigned Hall Calls",
      dataIndex: "lock_assigned_hall_calls",
      key: "lock_assigned_hall_calls",
      ...textFilterColumn<SocketMessageInterface>((row) =>
        [
          ...(row.hallCalls?.upAssigned?.frontLock ?? []),
          ...(row.hallCalls?.upAssigned?.rearLock ?? []),
          ...(row.hallCalls?.downAssigned?.frontLock ?? []),
          ...(row.hallCalls?.downAssigned?.rearLock ?? []),
        ].join(","),
      ),
      render: (text: boolean, row: SocketMessageInterface) => (
        <>
          {row.hallCalls.upAssigned.frontLock.map((hallCall: number) => (
            <Tag
              key={`up-assigned-front-lock-${hallCall}`}
              className="terminal__positionTag"
              color="lime"
            >
              {hallCall}
            </Tag>
          ))}
          {row.hallCalls.upAssigned.rearLock.map((hallCall: number) => (
            <Tag
              key={`up-assigned-rear-lock-${hallCall}`}
              className="terminal__positionTag"
              color="lime"
            >
              {hallCall}
            </Tag>
          ))}
          {row.hallCalls.downAssigned.frontLock.map((hallCall: number) => (
            <Tag
              key={`down-assigned-front-lock-${hallCall}`}
              className="terminal__positionTag"
              color="orange"
            >
              {hallCall}
            </Tag>
          ))}
          {row.hallCalls.downAssigned.rearLock.map((hallCall: number) => (
            <Tag
              key={`down-assigned-rear-lock-${hallCall}`}
              className="terminal__positionTag"
              color="orange"
            >
              {hallCall}
            </Tag>
          ))}
        </>
      ),
    },
  ];
};

export default TerminalTableColumns;
